import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import {
  useWsmOptionSetDeleteMutation,
  useWsmOptionSetDetailsQuery,
  useWsmOptionSetUpdateMutation,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { getMutationState } from "@dashboard/misc";
import { Skeleton } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import WsmOptionSetDetailsPage from "../components/WsmOptionSetDetailsPage/WsmOptionSetDetailsPage";
import { getWsmIndexedErrors } from "../errors";
import { sectionMessages } from "../messages";
import { wsmOptionSetListPath } from "../urls";
import { useTierGroups } from "../useTierGroups";
import { getOptionSetLabel, getOptionSetUpdateInput, optionSetToFormData } from "../utils";

export const WsmOptionSetDetailsView = ({ id }: { id: string }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const tierGroups = useTierGroups();
  const { data, loading } = useWsmOptionSetDetailsQuery({ variables: { id } });
  const [updateOptionSet, updateOptionSetOpts] = useWsmOptionSetUpdateMutation({
    onCompleted: result => {
      if (!result.wsmOptionSetUpdate?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
      }
    },
  });
  const [deleteOptionSet, deleteOptionSetOpts] = useWsmOptionSetDeleteMutation({
    onCompleted: result => {
      if (!result.wsmOptionSetDelete?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
        navigate(wsmOptionSetListPath);
      } else {
        setDeleteOpen(false);
      }
    },
  });
  const optionSet = data?.wsmOptionSet;
  const errors = updateOptionSetOpts.data?.wsmOptionSetUpdate?.errors ?? [];

  if (loading || !optionSet) {
    return (
      <DetailPageLayout>
        <TopNav
          href={wsmOptionSetListPath}
          title={intl.formatMessage(sectionMessages.optionSetListTitle)}
        />
        <DetailPageLayout.Content>
          <Skeleton />
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }

  return (
    <>
      <WsmOptionSetDetailsPage
        // Keyed on the row so a save that comes back with new choice IDs
        // re-seeds the form from the server rather than from stale drafts.
        key={`${optionSet.id}-${optionSet.values.length}`}
        title={getOptionSetLabel(optionSet)}
        initialData={optionSetToFormData(optionSet)}
        errors={errors}
        valueRowErrors={getWsmIndexedErrors(errors, "values")}
        tierGroups={tierGroups}
        disabled={updateOptionSetOpts.loading}
        saveButtonBarState={getMutationState(
          updateOptionSetOpts.called,
          updateOptionSetOpts.loading,
          errors,
        )}
        onSubmit={formData =>
          updateOptionSet({
            variables: { id: optionSet.id, input: getOptionSetUpdateInput(formData) },
          })
        }
        onDelete={() => setDeleteOpen(true)}
      />
      <ActionDialog
        open={deleteOpen}
        variant="delete"
        title={intl.formatMessage(sectionMessages.optionSetDeleteTitle)}
        confirmButtonState={getMutationState(
          deleteOptionSetOpts.called,
          deleteOptionSetOpts.loading,
          [],
        )}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteOptionSet({ variables: { id: optionSet.id } })}
      >
        {intl.formatMessage(sectionMessages.optionSetDeleteContent, {
          name: getOptionSetLabel(optionSet),
          count: optionSet.values.length,
        })}
      </ActionDialog>
    </>
  );
};

export default WsmOptionSetDetailsView;
